import { type ParticipantTask, ParticipantTaskStatus } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { DateTime, inflection, P } from '@piccolohealth/util';
import React from 'react';
import { FaRegClock } from 'react-icons/fa';

interface Props {
  task: ParticipantTask;
}

export const ParticipantTaskDate = (props: Props) => {
  const { task } = props;

  const isComplete = task.status === ParticipantTaskStatus.Complete;

  const dueDate = task.dueDate ? DateTime.fromISO(task.dueDate.toString()) : null;

  const text = P.run(() => {
    if (isComplete && task.completedAt) {
      const completedAt = DateTime.fromISO(task.completedAt.toString()).toFormat('dd/MM/yyyy');
      return `Completed on ${completedAt}`;
    }

    if (task.requiresDueDate && !dueDate) {
      return 'Requires a due date';
    }

    if (!task.requiresDueDate && !dueDate) {
      return 'No due date';
    }

    if (dueDate) {
      const dueDateFormat = dueDate.toFormat('dd/MM/yyyy');

      if (isComplete) {
        return dueDateFormat;
      }

      const durationDiff = dueDate.diffNow(['days', 'hours']);
      const humanizedDayDiff = durationDiff.mapUnits(Math.round).toHuman({
        listStyle: 'long',
        signDisplay: 'never',
      });
      const isInPast = durationDiff.toMillis() < 0;

      if (isInPast) {
        return `${dueDateFormat} (Overdue by ${humanizedDayDiff})`;
      } else {
        return `${dueDateFormat} (Due in ${humanizedDayDiff})`;
      }
    }

    return null;
  });

  const color = P.run(() => {
    if (isComplete) {
      return 'gray.500';
    }

    if (task.requiresDueDate && !dueDate) {
      return 'orange.400';
    }

    if (dueDate) {
      const dayDiff = Math.round(dueDate.diffNow('days').days);

      if (dayDiff < 0) {
        return 'red.500';
      }

      if (dayDiff < 3) {
        return 'orange.400';
      }

      if (dayDiff < 14) {
        return 'yellow.400';
      }
    }

    return 'gray.500';
  });

  const recurrence = P.run(() => {
    if (!task.recurrence || task.recurrence.intervalDays === 0) {
      return null;
    }

    return (
      <ChakraV3.HStack gap={1}>
        <FaRegClock />
        {task.recurrence.intervalDays} {inflection.inflect('days', task.recurrence.intervalDays)}
      </ChakraV3.HStack>
    );
  });

  return (
    <ChakraV3.HStack color={color} fontSize='xs' fontWeight='semibold' gap={2}>
      {text}
      {recurrence}
    </ChakraV3.HStack>
  );
};
